import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FileText, Eye, ScanText, Download, Sparkles, Search, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { documentsAPI, DOCUMENT_STATUS_LABELS, DOCUMENT_CATEGORY_LABELS } from '../lib/documentsAPI';
import { formatAUDate } from '../lib/dateUtils';
import { downloadBlob } from '../lib/fileUtils';
import { DataTable } from '../components/ds/DataTable';
import { Drawer, EmptyState, PageHeader } from '../components/ds/Primitives';
import { FilePreview } from './BasWorkspace';
import { ClientCombobox } from '../components/tasks/ClientCombobox';

// Stored review states mapped to the app-wide pill style (values unchanged).
const PILL = { uploaded: ['Uploaded', 'bg-muted text-muted-foreground'], needs_clarification: ['Needs review', 'bg-warning/15 text-warning'],
  reviewed: ['Reviewed', 'bg-primary/10 text-primary'], accepted: ['Processed', 'bg-success/15 text-success'] };
const SCAN = { pending: ['Scanning', 'bg-muted text-muted-foreground'], scanning: ['Scanning', 'bg-muted text-muted-foreground'], clean: ['Scan clean', 'bg-success/15 text-success'], infected: ['Blocked by scan', 'bg-destructive/10 text-destructive'] };
const Pill = ({ v, map, testId }) => { const [l, c] = map[v] || [v, 'bg-muted text-muted-foreground']; return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${c}`} data-testid={testId}><span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />{l}</span>; };
const sel = 'h-9 rounded-md border border-input bg-card px-2 text-sm';

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { search: locSearch } = useLocation();
  const [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(locSearch).get('status') || 'all');
  useEffect(() => { const s = new URLSearchParams(locSearch).get('status'); if (s) setStatusFilter(s); }, [locSearch]);
  const [search, setSearch] = useState('');
  const [cls, setCls] = useState({ client_id: '', scope_kind: '', financial_year: '', period_key: '', link_type: '' });
  const [clientLabel, setClientLabel] = useState('');
  const [role, setRole] = useState(null);
  const [drawer, setDrawer] = useState(null);
  useEffect(() => {
    api.get('/api/me').then((r) => setRole(r.data.active_membership?.role)).catch(() => {});
  }, []);
  const canEdit = role && role !== 'read_only';
  const fyOptions = Array.from({ length: 7 }, (_, i) => { const y = new Date().getFullYear() + (new Date().getMonth() >= 6 ? 1 : 0) - i; return `${y - 1}-${String(y).slice(2)}`; });
  const [ocrDoc, setOcrDoc] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      Object.entries(cls).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await documentsAPI.listAll(params);
      setDocs(res.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (docId, status) => {
    try {
      await documentsAPI.setStatus(docId, status);
      setDocs((prev) => prev.map((d) => (d.id === docId ? { ...d, status } : d)));
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleClassify = async (docId) => {
    try {
      const res = await documentsAPI.classify(docId);
      setDocs((prev) => prev.map((d) => (d.id === docId ? { ...d, category: res.data.category } : d)));
      toast.success(`Classified as ${DOCUMENT_CATEGORY_LABELS[res.data.category] || res.data.category}`);
    } catch (error) {
      toast.error('Classification failed');
    }
  };

  // Preview opens inline in a new tab; download saves the file to disk.
  const openBlob = async (docId, apiCall) => {
    try {
      const res = await apiCall(docId);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Failed to open document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await documentsAPI.download(doc.id);
      downloadBlob(res.data, doc.filename);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleOcr = async (doc) => {
    setOcrDoc(doc);
    setOcrResult(null);
    setOcrLoading(true);
    try {
      const res = await documentsAPI.ocr(doc.id);
      setOcrResult(res.data);
    } catch (error) {
      toast.error('Text extraction failed');
      setOcrResult({ text: '', message: 'Extraction failed.', char_count: 0 });
    } finally {
      setOcrLoading(false);
    }
  };

  const columns = [
    { key: 'filename', header: 'Document', sortable: true, cell: (d) => <span className="flex items-center gap-2 font-medium"><FileText className="h-4 w-4 shrink-0 text-muted-foreground" />{d.filename}{d.version > 1 && <span className="text-xs text-muted-foreground">v{d.version}</span>}</span> },
    { key: 'client_name', header: 'Client', sortable: true, cell: (d) => !d.client_id ? '—' : d.client_name ? <Link to={`/clients/${d.client_id}?tab=documents`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">{d.client_name}</Link> : <span className="text-xs text-muted-foreground" data-testid={`doc-client-removed-${d.id}`}>Client removed</span> },
    { key: 'category', header: 'Category', cell: (d) => d.category ? <span className="text-sm">{DOCUMENT_CATEGORY_LABELS[d.category] || d.category}</span> : <span className="text-xs text-muted-foreground">Unclassified</span> },
    { key: 'uploaded_at', header: 'Uploaded', sortable: true, cell: (d) => <span className="tabular text-muted-foreground">{formatAUDate(d.uploaded_at)}</span> },
    { key: 'uploaded_by_name', header: 'Uploaded by', cell: (d) => <span className="text-muted-foreground">{d.uploaded_by_name || (d.uploaded_by === 'system' ? 'System' : '—')}</span> },
    { key: 'status', header: 'Status', cell: (d) => (
      <div className="flex flex-wrap items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {canEdit ? (
          <Select value={d.status || 'uploaded'} onValueChange={(v) => handleStatus(d.id, v)}>
            <SelectTrigger className="h-7 w-[150px] border-transparent bg-transparent px-1 text-xs shadow-none" data-testid={`doc-status-${d.id}`}><SelectValue><Pill v={d.status || 'uploaded'} map={PILL} /></SelectValue></SelectTrigger>
            <SelectContent>{Object.keys(DOCUMENT_STATUS_LABELS).map((k) => <SelectItem key={k} value={k}>{PILL[k]?.[0] || DOCUMENT_STATUS_LABELS[k]}</SelectItem>)}</SelectContent>
          </Select>) : <Pill v={d.status || 'uploaded'} map={PILL} testId={`doc-status-${d.id}`} />}
        {d.scan_status && <Pill v={d.scan_status} map={SCAN} testId={`doc-scan-${d.id}`} />}
      </div>) },
    { key: 'actions', header: <span className="sr-only">Actions</span>, cell: (d) => (
      <div className="flex justify-end whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <Button size="icon" variant="ghost" title="Quick view" onClick={() => setDrawer(d)} data-testid={`quickview-${d.id}`}><Eye className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" title="Open in new tab" onClick={() => openBlob(d.id, documentsAPI.preview)} data-testid={`preview-${d.id}`}><ExternalLink className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" title="Extract text (OCR)" onClick={() => handleOcr(d)} data-testid={`ocr-${d.id}`}><ScanText className="h-4 w-4" /></Button>
        {canEdit && <Button size="icon" variant="ghost" title="Suggest category (rule-based classifier)" onClick={() => handleClassify(d.id)} data-testid={`classify-${d.id}`}><Sparkles className="h-4 w-4" /></Button>}
        <Button size="icon" variant="ghost" title="Download" onClick={() => handleDownload(d)} data-testid={`download-${d.id}`}><Download className="h-4 w-4" /></Button>
      </div>) },
  ];

  return (
    <div className="space-y-5 p-4 sm:p-6 lg:p-8">
      <PageHeader title={<span data-testid="documents-title">Documents</span>} description="All client documents across the firm. Review, preview, classify and extract text." />

      <div className="flex flex-wrap items-center gap-2" data-testid="documents-filters">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
          <Input className="pl-9" placeholder="Search filename…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="documents-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="documents-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(DOCUMENT_STATUS_LABELS).map((k) => <SelectItem key={k} value={k}>{PILL[k]?.[0] || DOCUMENT_STATUS_LABELS[k]}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="w-full sm:w-64" data-testid="documents-filter-entity"><ClientCombobox status={null} value={cls.client_id} label={clientLabel} placeholder="All entities" testid="documents-filter-client"
          onChange={(c) => { setClientLabel(c?.display_name || ''); setCls({ ...cls, client_id: c?.id || '' }); }} /></div>
        <select className={sel} value={cls.scope_kind} onChange={(e) => setCls({ ...cls, scope_kind: e.target.value })} data-testid="documents-filter-scope">
          <option value="">Permanent + period</option><option value="permanent">Permanent</option><option value="period">Period</option>
        </select>
        <select className={sel} value={cls.financial_year} onChange={(e) => setCls({ ...cls, financial_year: e.target.value })} data-testid="documents-filter-fy">
          <option value="">Any FY</option>{fyOptions.map((f) => <option key={f} value={f}>FY {f}</option>)}
        </select>
        <Input className="w-40" placeholder="Period (e.g. q:2027:1)" value={cls.period_key} onChange={(e) => setCls({ ...cls, period_key: e.target.value })} data-testid="documents-filter-period" />
        <select className={sel} value={cls.link_type} onChange={(e) => setCls({ ...cls, link_type: e.target.value })} data-testid="documents-filter-link">
          <option value="">Any workflow</option>
          {[['compliance_request', 'Compliance request'], ['tax_return', 'Tax return'], ['rental_fy', 'Rental FY'], ['rental_property', 'Rental property'], ['annual_reference', 'Annual reference'], ['conversation', 'Conversation']].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Button variant="outline" onClick={load} data-testid="documents-filter-apply">Apply</Button>
      </div>

      {loading ? (
        <div className="space-y-2 rounded-lg border border-border bg-card p-4" data-testid="documents-loading">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-8 animate-pulse rounded bg-muted" />)}</div>
      ) : (
        <DataTable testId="documents-table" storageKey="documents" rows={docs} columns={columns} onRowClick={setDrawer}
          empty={<EmptyState icon={FileText} title="No documents found" testId="documents-empty">Documents uploaded against a client (from the client's Documents tab) appear here.</EmptyState>}
          footer={<p className="border-t border-border px-3 py-2 text-xs text-muted-foreground tabular" data-testid="documents-count">{docs.length} document{docs.length === 1 ? '' : 's'}</p>} />
      )}

      <Drawer open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)} title={drawer?.filename} description={drawer && [drawer.client_name, drawer.uploaded_at && `Uploaded ${formatAUDate(drawer.uploaded_at)}`].filter(Boolean).join(' · ')} testId="document-drawer">
        {drawer && <div className="space-y-4">
          <div className="flex flex-wrap gap-2"><Pill v={drawer.status || 'uploaded'} map={PILL} />{drawer.category && <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{DOCUMENT_CATEGORY_LABELS[drawer.category] || drawer.category}</span>}</div>
          <FilePreview key={drawer.id} docId={drawer.id} testId="document-drawer-preview" />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => handleDownload(drawer)} data-testid="document-drawer-download"><Download className="mr-1.5 h-4 w-4" />Download</Button>
            <Button size="sm" variant="outline" onClick={() => openBlob(drawer.id, documentsAPI.preview)}><ExternalLink className="mr-1.5 h-4 w-4" />Open in new tab</Button>
            {drawer.client_id && <Button size="sm" variant="ghost" asChild><Link to={`/clients/${drawer.client_id}?tab=documents`}>Client documents</Link></Button>}
          </div>
        </div>}
      </Drawer>

      <Dialog open={!!ocrDoc} onOpenChange={(o) => !o && setOcrDoc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanText className="h-5 w-5" /> Extracted text — {ocrDoc?.filename}</DialogTitle></DialogHeader>
          {ocrLoading ? <div className="h-40 animate-pulse rounded bg-muted" /> : ocrResult ? (
            <div className="space-y-3">
              {ocrResult.message && <div className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning">{ocrResult.message}</div>}
              {ocrResult.text ? <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/50 p-3 text-xs">{ocrResult.text}</pre> : <p className="text-sm text-muted-foreground">No text could be extracted.</p>}
              <p className="text-xs text-muted-foreground">{ocrResult.char_count} characters · method: {ocrResult.method || 'n/a'}</p>
            </div>) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
